import { useMutation, type UseMutationOptions, useQuery, type UseQueryOptions } from "@tanstack/react-query";

import { useFetch } from "../fetcher";

import type * as Schema from "./CrowdCoursingApiSchemas";

export const CandidateSearchDocument = `
    query CandidateSearch($after: String, $before: String, $first: Int, $last: Int, $order: [ResumeSummaryRedactedSortInput!], $where: ResumeSummaryRedactedFilterInput) {
  resumesRedacted(
    after: $after
    before: $before
    first: $first
    last: $last
    order: $order
    where: $where
  ) {
    nodes {
      id
      userId
      certifications
      skills
      graduationYear
      gpaUnweighted
      gpaWeighted
      isFavorite
      isCandidate
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
    totalCount
  }
}
    `;

export const useCandidateSearchQuery = <TData = Schema.CandidateSearchQuery, TError = unknown>(
  variables?: Schema.CandidateSearchQueryVariables,
  options?: Omit<UseQueryOptions<Schema.CandidateSearchQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.CandidateSearchQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.CandidateSearchQuery, TError, TData>({
    queryFn: useFetch<Schema.CandidateSearchQuery, Schema.CandidateSearchQueryVariables>(CandidateSearchDocument).bind(
      null,
      variables,
    ),
    queryKey: variables === undefined ? ["CandidateSearch"] : ["CandidateSearch", variables],
    ...options,
  });
};

useCandidateSearchQuery.getKey = (variables?: Schema.CandidateSearchQueryVariables) =>
  variables === undefined ? ["CandidateSearch"] : ["CandidateSearch", variables];

export const EventUpcomingRsvpsDocument = `
    query EventUpcomingRsvps($eventId: ID!, $start: DateTime!, $end: DateTime!) {
  session(eventId: $eventId) {
    id
    eventUpcomingRsvps(start: $start, end: $end) {
      id
      meetingTime {
        id
        beginMeetingTime
        endMeetingTime
      }
      user {
        id
        phoneNumber
        email
        firstName
        lastName
      }
    }
  }
}
    `;

export const useEventUpcomingRsvpsQuery = <TData = Schema.EventUpcomingRsvpsQuery, TError = unknown>(
  variables: Schema.EventUpcomingRsvpsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.EventUpcomingRsvpsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.EventUpcomingRsvpsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.EventUpcomingRsvpsQuery, TError, TData>({
    queryFn: useFetch<Schema.EventUpcomingRsvpsQuery, Schema.EventUpcomingRsvpsQueryVariables>(
      EventUpcomingRsvpsDocument,
    ).bind(null, variables),
    queryKey: ["EventUpcomingRsvps", variables],
    ...options,
  });
};

useEventUpcomingRsvpsQuery.getKey = (variables: Schema.EventUpcomingRsvpsQueryVariables) => [
  "EventUpcomingRsvps",
  variables,
];

export const DeleteEventResourceDocument = `
    mutation DeleteEventResource($request: DeleteEventResourceRequestInput!) {
  deleteEventResource(input: {request: $request}) {
    deleteEventResourceResult {
      wasDeleted
    }
  }
}
    `;

export const useDeleteEventResourceMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Schema.DeleteEventResourceMutation,
    TError,
    Schema.DeleteEventResourceMutationVariables,
    TContext
  >,
) => {
  return useMutation<Schema.DeleteEventResourceMutation, TError, Schema.DeleteEventResourceMutationVariables, TContext>(
    {
      mutationFn: useFetch<Schema.DeleteEventResourceMutation, Schema.DeleteEventResourceMutationVariables>(
        DeleteEventResourceDocument,
      ),
      mutationKey: ["DeleteEventResource"],
      ...options,
    },
  );
};

useDeleteEventResourceMutation.getKey = () => ["DeleteEventResource"];

export const SaveEventResourceDocument = `
    mutation SaveEventResource($request: SaveEventResourceRequestInput!) {
  saveEventResource(input: {request: $request}) {
    resourceSummary {
      id
    }
  }
}
    `;

export const useSaveEventResourceMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Schema.SaveEventResourceMutation,
    TError,
    Schema.SaveEventResourceMutationVariables,
    TContext
  >,
) => {
  return useMutation<Schema.SaveEventResourceMutation, TError, Schema.SaveEventResourceMutationVariables, TContext>({
    mutationFn: useFetch<Schema.SaveEventResourceMutation, Schema.SaveEventResourceMutationVariables>(
      SaveEventResourceDocument,
    ),
    mutationKey: ["SaveEventResource"],
    ...options,
  });
};

useSaveEventResourceMutation.getKey = () => ["SaveEventResource"];

export const DeleteRoomResourceDocument = `
    mutation DeleteRoomResource($request: DeleteRoomResourceRequestInput!) {
  deleteRoomResource(input: {request: $request}) {
    deleteRoomResourceResult {
      wasDeleted
    }
  }
}
    `;

export const useDeleteRoomResourceMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Schema.DeleteRoomResourceMutation,
    TError,
    Schema.DeleteRoomResourceMutationVariables,
    TContext
  >,
) => {
  return useMutation<Schema.DeleteRoomResourceMutation, TError, Schema.DeleteRoomResourceMutationVariables, TContext>({
    mutationFn: useFetch<Schema.DeleteRoomResourceMutation, Schema.DeleteRoomResourceMutationVariables>(
      DeleteRoomResourceDocument,
    ),
    mutationKey: ["DeleteRoomResource"],
    ...options,
  });
};

useDeleteRoomResourceMutation.getKey = () => ["DeleteRoomResource"];

export const SaveRoomResourceDocument = `
    mutation SaveRoomResource($request: SaveRoomResourceRequestInput!) {
  saveRoomResource(input: {request: $request}) {
    resourceSummary {
      id
    }
  }
}
    `;

export const useSaveRoomResourceMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Schema.SaveRoomResourceMutation,
    TError,
    Schema.SaveRoomResourceMutationVariables,
    TContext
  >,
) => {
  return useMutation<Schema.SaveRoomResourceMutation, TError, Schema.SaveRoomResourceMutationVariables, TContext>({
    mutationFn: useFetch<Schema.SaveRoomResourceMutation, Schema.SaveRoomResourceMutationVariables>(
      SaveRoomResourceDocument,
    ),
    mutationKey: ["SaveRoomResource"],
    ...options,
  });
};

useSaveRoomResourceMutation.getKey = () => ["SaveRoomResource"];

export const AdminSelectDocument = `
    query AdminSelect {
  mySchoolDistricts {
    id
    schoolDistrictId
    bubbleId
    name
    avatarUri
  }
  mySchools {
    id
    bubbleId
    name
    avatarUri
  }
}
    `;

export const useAdminSelectQuery = <TData = Schema.AdminSelectQuery, TError = unknown>(
  variables?: Schema.AdminSelectQueryVariables,
  options?: Omit<UseQueryOptions<Schema.AdminSelectQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.AdminSelectQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.AdminSelectQuery, TError, TData>({
    queryFn: useFetch<Schema.AdminSelectQuery, Schema.AdminSelectQueryVariables>(AdminSelectDocument).bind(
      null,
      variables,
    ),
    queryKey: variables === undefined ? ["AdminSelect"] : ["AdminSelect", variables],
    ...options,
  });
};

useAdminSelectQuery.getKey = (variables?: Schema.AdminSelectQueryVariables) =>
  variables === undefined ? ["AdminSelect"] : ["AdminSelect", variables];

export const MyBackgroundChecksDocument = `
    query MyBackgroundChecks {
  me {
    id
    backgroundChecks {
      id
      schoolDistrictId
      schoolDistrictName
      url
      isCompleted
    }
  }
  activeSchoolDistricts {
    schoolDistrictId
    bubbleId
    name
  }
}
    `;

export const useMyBackgroundChecksQuery = <TData = Schema.MyBackgroundChecksQuery, TError = unknown>(
  variables?: Schema.MyBackgroundChecksQueryVariables,
  options?: Omit<UseQueryOptions<Schema.MyBackgroundChecksQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.MyBackgroundChecksQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.MyBackgroundChecksQuery, TError, TData>({
    queryFn: useFetch<Schema.MyBackgroundChecksQuery, Schema.MyBackgroundChecksQueryVariables>(
      MyBackgroundChecksDocument,
    ).bind(null, variables),
    queryKey: variables === undefined ? ["MyBackgroundChecks"] : ["MyBackgroundChecks", variables],
    ...options,
  });
};

useMyBackgroundChecksQuery.getKey = (variables?: Schema.MyBackgroundChecksQueryVariables) =>
  variables === undefined ? ["MyBackgroundChecks"] : ["MyBackgroundChecks", variables];

export const DistrictAdminMembersDocument = `
    query DistrictAdminMembers($schoolDistrictId: ID!) {
  schoolDistrict(schoolDistrictId: $schoolDistrictId) {
    bubbleId
    id
    members {
      email
      id
      roles
      isVetted
      firstName
      lastName
      phoneNumber
      schools {
        bubbleId
        id
        name
      }
    }
    name
    schoolDistrictId
  }
}
    `;

export const useDistrictAdminMembersQuery = <TData = Schema.DistrictAdminMembersQuery, TError = unknown>(
  variables: Schema.DistrictAdminMembersQueryVariables,
  options?: Omit<UseQueryOptions<Schema.DistrictAdminMembersQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.DistrictAdminMembersQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.DistrictAdminMembersQuery, TError, TData>({
    queryFn: useFetch<Schema.DistrictAdminMembersQuery, Schema.DistrictAdminMembersQueryVariables>(
      DistrictAdminMembersDocument,
    ).bind(null, variables),
    queryKey: ["DistrictAdminMembers", variables],
    ...options,
  });
};

useDistrictAdminMembersQuery.getKey = (variables: Schema.DistrictAdminMembersQueryVariables) => [
  "DistrictAdminMembers",
  variables,
];

export const DistrictAdminSchoolsDocument = `
    query DistrictAdminSchools($schoolDistrictId: ID!) {
  schoolDistrict(schoolDistrictId: $schoolDistrictId) {
    bubbleId
    id
    name
    schoolDistrictId
    schools {
      id
      name
      bubbleId
      memberCount
      requestCount
      city
      zipCode
    }
  }
}
    `;

export const useDistrictAdminSchoolsQuery = <TData = Schema.DistrictAdminSchoolsQuery, TError = unknown>(
  variables: Schema.DistrictAdminSchoolsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.DistrictAdminSchoolsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.DistrictAdminSchoolsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.DistrictAdminSchoolsQuery, TError, TData>({
    queryFn: useFetch<Schema.DistrictAdminSchoolsQuery, Schema.DistrictAdminSchoolsQueryVariables>(
      DistrictAdminSchoolsDocument,
    ).bind(null, variables),
    queryKey: ["DistrictAdminSchools", variables],
    ...options,
  });
};

useDistrictAdminSchoolsQuery.getKey = (variables: Schema.DistrictAdminSchoolsQueryVariables) => [
  "DistrictAdminSchools",
  variables,
];

export const EventQrDocument = `
    query EventQR($eventId: ID!) {
  session(eventId: $eventId) {
    id
    name
    checkInImageUri
  }
}
    `;

export const useEventQrQuery = <TData = Schema.EventQrQuery, TError = unknown>(
  variables: Schema.EventQrQueryVariables,
  options?: Omit<UseQueryOptions<Schema.EventQrQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.EventQrQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.EventQrQuery, TError, TData>({
    queryFn: useFetch<Schema.EventQrQuery, Schema.EventQrQueryVariables>(EventQrDocument).bind(null, variables),
    queryKey: ["EventQR", variables],
    ...options,
  });
};

useEventQrQuery.getKey = (variables: Schema.EventQrQueryVariables) => ["EventQR", variables];

export const EventRoomSummaryDocument = `
    query eventRoomSummary($roomId: ID!) {
  room(roomId: $roomId) {
    id
    name
  }
}
    `;

export const useEventRoomSummaryQuery = <TData = Schema.EventRoomSummaryQuery, TError = unknown>(
  variables: Schema.EventRoomSummaryQueryVariables,
  options?: Omit<UseQueryOptions<Schema.EventRoomSummaryQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.EventRoomSummaryQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.EventRoomSummaryQuery, TError, TData>({
    queryFn: useFetch<Schema.EventRoomSummaryQuery, Schema.EventRoomSummaryQueryVariables>(
      EventRoomSummaryDocument,
    ).bind(null, variables),
    queryKey: ["eventRoomSummary", variables],
    ...options,
  });
};

useEventRoomSummaryQuery.getKey = (variables: Schema.EventRoomSummaryQueryVariables) => ["eventRoomSummary", variables];

export const GetSessionRulesDocument = `
    query GetSessionRules($eventId: ID!) {
  session(eventId: $eventId) {
    rules {
      description
      name
      order
    }
  }
}
    `;

export const useGetSessionRulesQuery = <TData = Schema.GetSessionRulesQuery, TError = unknown>(
  variables: Schema.GetSessionRulesQueryVariables,
  options?: Omit<UseQueryOptions<Schema.GetSessionRulesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.GetSessionRulesQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.GetSessionRulesQuery, TError, TData>({
    queryFn: useFetch<Schema.GetSessionRulesQuery, Schema.GetSessionRulesQueryVariables>(GetSessionRulesDocument).bind(
      null,
      variables,
    ),
    queryKey: ["GetSessionRules", variables],
    ...options,
  });
};

useGetSessionRulesQuery.getKey = (variables: Schema.GetSessionRulesQueryVariables) => ["GetSessionRules", variables];

export const MyBubblesDocument = `
    query MyBubbles {
  me {
    id
    bubbles {
      id
      name
    }
  }
}
    `;

export const useMyBubblesQuery = <TData = Schema.MyBubblesQuery, TError = unknown>(
  variables?: Schema.MyBubblesQueryVariables,
  options?: Omit<UseQueryOptions<Schema.MyBubblesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.MyBubblesQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.MyBubblesQuery, TError, TData>({
    queryFn: useFetch<Schema.MyBubblesQuery, Schema.MyBubblesQueryVariables>(MyBubblesDocument).bind(null, variables),
    queryKey: variables === undefined ? ["MyBubbles"] : ["MyBubbles", variables],
    ...options,
  });
};

useMyBubblesQuery.getKey = (variables?: Schema.MyBubblesQueryVariables) =>
  variables === undefined ? ["MyBubbles"] : ["MyBubbles", variables];

export const MyScheduleOwnerDocument = `
    query MyScheduleOwner {
  myRooms {
    id
    name
    description
    sessions {
      id
      name
      meetingTimes {
        id
        beginMeetingTime
        endMeetingTime
        rsvps {
          firstName
          lastName
          email
        }
      }
    }
  }
}
    `;

export const useMyScheduleOwnerQuery = <TData = Schema.MyScheduleOwnerQuery, TError = unknown>(
  variables?: Schema.MyScheduleOwnerQueryVariables,
  options?: Omit<UseQueryOptions<Schema.MyScheduleOwnerQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.MyScheduleOwnerQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.MyScheduleOwnerQuery, TError, TData>({
    queryFn: useFetch<Schema.MyScheduleOwnerQuery, Schema.MyScheduleOwnerQueryVariables>(MyScheduleOwnerDocument).bind(
      null,
      variables,
    ),
    queryKey: variables === undefined ? ["MyScheduleOwner"] : ["MyScheduleOwner", variables],
    ...options,
  });
};

useMyScheduleOwnerQuery.getKey = (variables?: Schema.MyScheduleOwnerQueryVariables) =>
  variables === undefined ? ["MyScheduleOwner"] : ["MyScheduleOwner", variables];

export const MySchoolDistrictsDocument = `
    query MySchoolDistricts {
  mySchoolDistricts {
    id
    schoolDistrictId
    bubbleId
    name
    avatarUri
  }
}
    `;

export const useMySchoolDistrictsQuery = <TData = Schema.MySchoolDistrictsQuery, TError = unknown>(
  variables?: Schema.MySchoolDistrictsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.MySchoolDistrictsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.MySchoolDistrictsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.MySchoolDistrictsQuery, TError, TData>({
    queryFn: useFetch<Schema.MySchoolDistrictsQuery, Schema.MySchoolDistrictsQueryVariables>(
      MySchoolDistrictsDocument,
    ).bind(null, variables),
    queryKey: variables === undefined ? ["MySchoolDistricts"] : ["MySchoolDistricts", variables],
    ...options,
  });
};

useMySchoolDistrictsQuery.getKey = (variables?: Schema.MySchoolDistrictsQueryVariables) =>
  variables === undefined ? ["MySchoolDistricts"] : ["MySchoolDistricts", variables];

export const MySchoolDistrictsRequestsDocument = `
    query MySchoolDistrictsRequests {
  mySchoolDistricts {
    id
    schoolDistrictId
    bubbleId
    name
    schools {
      id
      bubbleId
      name
      requests {
        id
        firstName
        lastName
        email
      }
    }
  }
}
    `;

export const useMySchoolDistrictsRequestsQuery = <TData = Schema.MySchoolDistrictsRequestsQuery, TError = unknown>(
  variables?: Schema.MySchoolDistrictsRequestsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.MySchoolDistrictsRequestsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.MySchoolDistrictsRequestsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.MySchoolDistrictsRequestsQuery, TError, TData>({
    queryFn: useFetch<Schema.MySchoolDistrictsRequestsQuery, Schema.MySchoolDistrictsRequestsQueryVariables>(
      MySchoolDistrictsRequestsDocument,
    ).bind(null, variables),
    queryKey: variables === undefined ? ["MySchoolDistrictsRequests"] : ["MySchoolDistrictsRequests", variables],
    ...options,
  });
};

useMySchoolDistrictsRequestsQuery.getKey = (variables?: Schema.MySchoolDistrictsRequestsQueryVariables) =>
  variables === undefined ? ["MySchoolDistrictsRequests"] : ["MySchoolDistrictsRequests", variables];

export const MySchoolDistrictsSchoolsDocument = `
    query MySchoolDistrictsSchools {
  mySchoolDistricts {
    schoolDistrictId
    schools {
      id
      name
      memberCount
      requestCount
    }
  }
}
    `;

export const useMySchoolDistrictsSchoolsQuery = <TData = Schema.MySchoolDistrictsSchoolsQuery, TError = unknown>(
  variables?: Schema.MySchoolDistrictsSchoolsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.MySchoolDistrictsSchoolsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.MySchoolDistrictsSchoolsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.MySchoolDistrictsSchoolsQuery, TError, TData>({
    queryFn: useFetch<Schema.MySchoolDistrictsSchoolsQuery, Schema.MySchoolDistrictsSchoolsQueryVariables>(
      MySchoolDistrictsSchoolsDocument,
    ).bind(null, variables),
    queryKey: variables === undefined ? ["MySchoolDistrictsSchools"] : ["MySchoolDistrictsSchools", variables],
    ...options,
  });
};

useMySchoolDistrictsSchoolsQuery.getKey = (variables?: Schema.MySchoolDistrictsSchoolsQueryVariables) =>
  variables === undefined ? ["MySchoolDistrictsSchools"] : ["MySchoolDistrictsSchools", variables];

export const GetMySchoolsDocument = `
    query GetMySchools {
  mySchools {
    id
    bubbleId
    name
    memberCount
    requestCount
    avatarUri
  }
}
    `;

export const useGetMySchoolsQuery = <TData = Schema.GetMySchoolsQuery, TError = unknown>(
  variables?: Schema.GetMySchoolsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.GetMySchoolsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.GetMySchoolsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.GetMySchoolsQuery, TError, TData>({
    queryFn: useFetch<Schema.GetMySchoolsQuery, Schema.GetMySchoolsQueryVariables>(GetMySchoolsDocument).bind(
      null,
      variables,
    ),
    queryKey: variables === undefined ? ["GetMySchools"] : ["GetMySchools", variables],
    ...options,
  });
};

useGetMySchoolsQuery.getKey = (variables?: Schema.GetMySchoolsQueryVariables) =>
  variables === undefined ? ["GetMySchools"] : ["GetMySchools", variables];

export const NavigationBarDocument = `
    query NavigationBar {
  me {
    avatarUrl
    firstName
    id
    isAdmin
    isEducator
    isSiteAdmin
    isDistrictAdmin
    isSchoolAdmin
    lastName
    roles
  }
}
    `;

export const useNavigationBarQuery = <TData = Schema.NavigationBarQuery, TError = unknown>(
  variables?: Schema.NavigationBarQueryVariables,
  options?: Omit<UseQueryOptions<Schema.NavigationBarQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.NavigationBarQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.NavigationBarQuery, TError, TData>({
    queryFn: useFetch<Schema.NavigationBarQuery, Schema.NavigationBarQueryVariables>(NavigationBarDocument).bind(
      null,
      variables,
    ),
    queryKey: variables === undefined ? ["NavigationBar"] : ["NavigationBar", variables],
    ...options,
  });
};

useNavigationBarQuery.getKey = (variables?: Schema.NavigationBarQueryVariables) =>
  variables === undefined ? ["NavigationBar"] : ["NavigationBar", variables];

export const GetOrganizationsDocument = `
    query GetOrganizations {
  organizations {
    id
    bubbleId
    name
    address
    description
    slug
    type
  }
}
    `;

export const useGetOrganizationsQuery = <TData = Schema.GetOrganizationsQuery, TError = unknown>(
  variables?: Schema.GetOrganizationsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.GetOrganizationsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.GetOrganizationsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.GetOrganizationsQuery, TError, TData>({
    queryFn: useFetch<Schema.GetOrganizationsQuery, Schema.GetOrganizationsQueryVariables>(
      GetOrganizationsDocument,
    ).bind(null, variables),
    queryKey: variables === undefined ? ["GetOrganizations"] : ["GetOrganizations", variables],
    ...options,
  });
};

useGetOrganizationsQuery.getKey = (variables?: Schema.GetOrganizationsQueryVariables) =>
  variables === undefined ? ["GetOrganizations"] : ["GetOrganizations", variables];

export const RoomEventFieldsDocument = `
    query RoomEventFields($roomId: ID!) {
  room(roomId: $roomId) {
    id
    name
  }
}
    `;

export const useRoomEventFieldsQuery = <TData = Schema.RoomEventFieldsQuery, TError = unknown>(
  variables: Schema.RoomEventFieldsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.RoomEventFieldsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.RoomEventFieldsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.RoomEventFieldsQuery, TError, TData>({
    queryFn: useFetch<Schema.RoomEventFieldsQuery, Schema.RoomEventFieldsQueryVariables>(RoomEventFieldsDocument).bind(
      null,
      variables,
    ),
    queryKey: ["RoomEventFields", variables],
    ...options,
  });
};

useRoomEventFieldsQuery.getKey = (variables: Schema.RoomEventFieldsQueryVariables) => ["RoomEventFields", variables];

export const RoomMemberDocument = `
    query RoomMember($roomId: ID!, $userId: ID!) {
  room(roomId: $roomId) {
    id
    rsvps(userId: $userId) {
      id
      eventName
      beginMeetingTime
      endMeetingTime
    }
  }
}
    `;

export const useRoomMemberQuery = <TData = Schema.RoomMemberQuery, TError = unknown>(
  variables: Schema.RoomMemberQueryVariables,
  options?: Omit<UseQueryOptions<Schema.RoomMemberQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.RoomMemberQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.RoomMemberQuery, TError, TData>({
    queryFn: useFetch<Schema.RoomMemberQuery, Schema.RoomMemberQueryVariables>(RoomMemberDocument).bind(
      null,
      variables,
    ),
    queryKey: ["RoomMember", variables],
    ...options,
  });
};

useRoomMemberQuery.getKey = (variables: Schema.RoomMemberQueryVariables) => ["RoomMember", variables];

export const RoomMemberManagementDocument = `
    query RoomMemberManagement($id: ID!) {
  room(roomId: $id) {
    isPublic
    facilitators {
      id
    }
    members {
      id
      roles
      isVetted
      email
      firstName
      lastName
    }
    owner {
      id
      roles
      isVetted
      email
      firstName
      lastName
    }
    requests {
      id
      roles
      isVetted
      email
      firstName
      lastName
    }
  }
}
    `;

export const useRoomMemberManagementQuery = <TData = Schema.RoomMemberManagementQuery, TError = unknown>(
  variables: Schema.RoomMemberManagementQueryVariables,
  options?: Omit<UseQueryOptions<Schema.RoomMemberManagementQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.RoomMemberManagementQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.RoomMemberManagementQuery, TError, TData>({
    queryFn: useFetch<Schema.RoomMemberManagementQuery, Schema.RoomMemberManagementQueryVariables>(
      RoomMemberManagementDocument,
    ).bind(null, variables),
    queryKey: ["RoomMemberManagement", variables],
    ...options,
  });
};

useRoomMemberManagementQuery.getKey = (variables: Schema.RoomMemberManagementQueryVariables) => [
  "RoomMemberManagement",
  variables,
];

export const RoomMembersDocument = `
    query RoomMembers($id: ID!) {
  room(roomId: $id) {
    isPublic
    facilitators {
      id
    }
    members {
      id
      roles
      isVetted
      email
      firstName
      lastName
    }
    owner {
      id
      roles
      isVetted
      email
      firstName
      lastName
    }
  }
}
    `;

export const useRoomMembersQuery = <TData = Schema.RoomMembersQuery, TError = unknown>(
  variables: Schema.RoomMembersQueryVariables,
  options?: Omit<UseQueryOptions<Schema.RoomMembersQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.RoomMembersQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.RoomMembersQuery, TError, TData>({
    queryFn: useFetch<Schema.RoomMembersQuery, Schema.RoomMembersQueryVariables>(RoomMembersDocument).bind(
      null,
      variables,
    ),
    queryKey: ["RoomMembers", variables],
    ...options,
  });
};

useRoomMembersQuery.getKey = (variables: Schema.RoomMembersQueryVariables) => ["RoomMembers", variables];

export const RoomRequestsDocument = `
    query RoomRequests($id: ID!) {
  room(roomId: $id) {
    requests {
      id
      roles
      isVetted
      email
      firstName
      lastName
    }
  }
}
    `;

export const useRoomRequestsQuery = <TData = Schema.RoomRequestsQuery, TError = unknown>(
  variables: Schema.RoomRequestsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.RoomRequestsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.RoomRequestsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.RoomRequestsQuery, TError, TData>({
    queryFn: useFetch<Schema.RoomRequestsQuery, Schema.RoomRequestsQueryVariables>(RoomRequestsDocument).bind(
      null,
      variables,
    ),
    queryKey: ["RoomRequests", variables],
    ...options,
  });
};

useRoomRequestsQuery.getKey = (variables: Schema.RoomRequestsQueryVariables) => ["RoomRequests", variables];

export const RoomSessionsDocument = `
    query RoomSessions($roomId: ID!) {
  room(roomId: $roomId) {
    id
    userIsFacilitator
    userIsAdmin
    name
    sessions {
      id
      bubbleId
      roomId
      name
      description
      maxParticipants
      userIsFacilitator
      userIsMember
      userIsVetted
      meetingTimes {
        capacity
        id
        seriesId
        beginMeetingTime
        endMeetingTime
        userHasRsvpd
        eventId
      }
    }
  }
}
    `;

export const useRoomSessionsQuery = <TData = Schema.RoomSessionsQuery, TError = unknown>(
  variables: Schema.RoomSessionsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.RoomSessionsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.RoomSessionsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.RoomSessionsQuery, TError, TData>({
    queryFn: useFetch<Schema.RoomSessionsQuery, Schema.RoomSessionsQueryVariables>(RoomSessionsDocument).bind(
      null,
      variables,
    ),
    queryKey: ["RoomSessions", variables],
    ...options,
  });
};

useRoomSessionsQuery.getKey = (variables: Schema.RoomSessionsQueryVariables) => ["RoomSessions", variables];

export const RoomSummaryDocument = `
    query RoomSummary($id: ID!) {
  room(roomId: $id) {
    bubbleId
    userIsFacilitator
    userIsOwner
    userIsMember
    userIsPending
    userIsAdmin
    name
    isPublic
    isSearchable
    id
    description
    roomMilestones {
      id
      description
      dueDate
      name
      roomId
    }
    roomTasks {
      id
      description
      dueDate
      name
      roomId
      userId
    }
    owner {
      id
    }
    sessions {
      id
      name
      nextMeetingTime {
        capacity
        id
        beginMeetingTime
        endMeetingTime
      }
    }
    resources {
      id
      name
      description
      url
    }
  }
}
    `;

export const useRoomSummaryQuery = <TData = Schema.RoomSummaryQuery, TError = unknown>(
  variables: Schema.RoomSummaryQueryVariables,
  options?: Omit<UseQueryOptions<Schema.RoomSummaryQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.RoomSummaryQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.RoomSummaryQuery, TError, TData>({
    queryFn: useFetch<Schema.RoomSummaryQuery, Schema.RoomSummaryQueryVariables>(RoomSummaryDocument).bind(
      null,
      variables,
    ),
    queryKey: ["RoomSummary", variables],
    ...options,
  });
};

useRoomSummaryQuery.getKey = (variables: Schema.RoomSummaryQueryVariables) => ["RoomSummary", variables];

export const SchoolAdminSchoolMembersDocument = `
    query SchoolAdminSchoolMembers($id: ID!) {
  school(schoolId: $id) {
    name
    bubbleId
    members {
      id
      email
      phoneNumber
      firstName
      lastName
      roles
      isVetted
    }
  }
}
    `;

export const useSchoolAdminSchoolMembersQuery = <TData = Schema.SchoolAdminSchoolMembersQuery, TError = unknown>(
  variables: Schema.SchoolAdminSchoolMembersQueryVariables,
  options?: Omit<UseQueryOptions<Schema.SchoolAdminSchoolMembersQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.SchoolAdminSchoolMembersQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.SchoolAdminSchoolMembersQuery, TError, TData>({
    queryFn: useFetch<Schema.SchoolAdminSchoolMembersQuery, Schema.SchoolAdminSchoolMembersQueryVariables>(
      SchoolAdminSchoolMembersDocument,
    ).bind(null, variables),
    queryKey: ["SchoolAdminSchoolMembers", variables],
    ...options,
  });
};

useSchoolAdminSchoolMembersQuery.getKey = (variables: Schema.SchoolAdminSchoolMembersQueryVariables) => [
  "SchoolAdminSchoolMembers",
  variables,
];

export const SchoolAdminSchoolRequestsDocument = `
    query SchoolAdminSchoolRequests($id: ID!) {
  school(schoolId: $id) {
    name
    bubbleId
    requests {
      id
      email
      phoneNumber
      firstName
      lastName
    }
  }
}
    `;

export const useSchoolAdminSchoolRequestsQuery = <TData = Schema.SchoolAdminSchoolRequestsQuery, TError = unknown>(
  variables: Schema.SchoolAdminSchoolRequestsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.SchoolAdminSchoolRequestsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.SchoolAdminSchoolRequestsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.SchoolAdminSchoolRequestsQuery, TError, TData>({
    queryFn: useFetch<Schema.SchoolAdminSchoolRequestsQuery, Schema.SchoolAdminSchoolRequestsQueryVariables>(
      SchoolAdminSchoolRequestsDocument,
    ).bind(null, variables),
    queryKey: ["SchoolAdminSchoolRequests", variables],
    ...options,
  });
};

useSchoolAdminSchoolRequestsQuery.getKey = (variables: Schema.SchoolAdminSchoolRequestsQueryVariables) => [
  "SchoolAdminSchoolRequests",
  variables,
];

export const SchoolAdminSchoolRsvpsDocument = `
    query SchoolAdminSchoolRsvps($id: ID!) {
  school(schoolId: $id) {
    rsvps {
      beginMeetingTime
      endMeetingTime
      id
      eventName
      user {
        firstName
        lastName
        phoneNumber
        email
      }
    }
  }
}
    `;

export const useSchoolAdminSchoolRsvpsQuery = <TData = Schema.SchoolAdminSchoolRsvpsQuery, TError = unknown>(
  variables: Schema.SchoolAdminSchoolRsvpsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.SchoolAdminSchoolRsvpsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.SchoolAdminSchoolRsvpsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.SchoolAdminSchoolRsvpsQuery, TError, TData>({
    queryFn: useFetch<Schema.SchoolAdminSchoolRsvpsQuery, Schema.SchoolAdminSchoolRsvpsQueryVariables>(
      SchoolAdminSchoolRsvpsDocument,
    ).bind(null, variables),
    queryKey: ["SchoolAdminSchoolRsvps", variables],
    ...options,
  });
};

useSchoolAdminSchoolRsvpsQuery.getKey = (variables: Schema.SchoolAdminSchoolRsvpsQueryVariables) => [
  "SchoolAdminSchoolRsvps",
  variables,
];

export const SchoolMemberManagementDocument = `
    query SchoolMemberManagement($id: ID!) {
  school(schoolId: $id) {
    name
    bubbleId
    members {
      id
      email
      phoneNumber
      firstName
      lastName
      roles
      isVetted
    }
    requests {
      id
      email
      phoneNumber
      firstName
      lastName
      roles
      isVetted
    }
  }
}
    `;

export const useSchoolMemberManagementQuery = <TData = Schema.SchoolMemberManagementQuery, TError = unknown>(
  variables: Schema.SchoolMemberManagementQueryVariables,
  options?: Omit<UseQueryOptions<Schema.SchoolMemberManagementQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.SchoolMemberManagementQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.SchoolMemberManagementQuery, TError, TData>({
    queryFn: useFetch<Schema.SchoolMemberManagementQuery, Schema.SchoolMemberManagementQueryVariables>(
      SchoolMemberManagementDocument,
    ).bind(null, variables),
    queryKey: ["SchoolMemberManagement", variables],
    ...options,
  });
};

useSchoolMemberManagementQuery.getKey = (variables: Schema.SchoolMemberManagementQueryVariables) => [
  "SchoolMemberManagement",
  variables,
];

export const SearchOptionsDocument = `
    query SearchOptions {
  grades: gradeLevels
  meetingDays
  subjects
  activeSchoolDistricts {
    id
    name
    schoolDistrictId
  }
}
    `;

export const useSearchOptionsQuery = <TData = Schema.SearchOptionsQuery, TError = unknown>(
  variables?: Schema.SearchOptionsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.SearchOptionsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.SearchOptionsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.SearchOptionsQuery, TError, TData>({
    queryFn: useFetch<Schema.SearchOptionsQuery, Schema.SearchOptionsQueryVariables>(SearchOptionsDocument).bind(
      null,
      variables,
    ),
    queryKey: variables === undefined ? ["SearchOptions"] : ["SearchOptions", variables],
    ...options,
  });
};

useSearchOptionsQuery.getKey = (variables?: Schema.SearchOptionsQueryVariables) =>
  variables === undefined ? ["SearchOptions"] : ["SearchOptions", variables];

export const SeriesMeetingsDocument = `
    query SeriesMeetings($eventId: ID!, $seriesId: ID!) {
  session(eventId: $eventId) {
    id
    series(seriesId: $seriesId) {
      id
      meetingTimes {
        id
        eventName
        beginMeetingTime
        endMeetingTime
      }
    }
  }
}
    `;

export const useSeriesMeetingsQuery = <TData = Schema.SeriesMeetingsQuery, TError = unknown>(
  variables: Schema.SeriesMeetingsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.SeriesMeetingsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.SeriesMeetingsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.SeriesMeetingsQuery, TError, TData>({
    queryFn: useFetch<Schema.SeriesMeetingsQuery, Schema.SeriesMeetingsQueryVariables>(SeriesMeetingsDocument).bind(
      null,
      variables,
    ),
    queryKey: ["SeriesMeetings", variables],
    ...options,
  });
};

useSeriesMeetingsQuery.getKey = (variables: Schema.SeriesMeetingsQueryVariables) => ["SeriesMeetings", variables];

export const EventEditDocument = `
    query eventEdit($eventId: ID!) {
  session(eventId: $eventId) {
    id
    name
    description
    isSearchable
    isPublic
    bubbleId
    roomId
    allMeetingTimes {
      capacity
      id
      beginMeetingTime
      endMeetingTime
      seriesId
      eventId
    }
  }
}
    `;

export const useEventEditQuery = <TData = Schema.EventEditQuery, TError = unknown>(
  variables: Schema.EventEditQueryVariables,
  options?: Omit<UseQueryOptions<Schema.EventEditQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.EventEditQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.EventEditQuery, TError, TData>({
    queryFn: useFetch<Schema.EventEditQuery, Schema.EventEditQueryVariables>(EventEditDocument).bind(null, variables),
    queryKey: ["eventEdit", variables],
    ...options,
  });
};

useEventEditQuery.getKey = (variables: Schema.EventEditQueryVariables) => ["eventEdit", variables];

export const SessionMemberDocument = `
    query SessionMember($eventId: ID!, $userId: ID!) {
  session(eventId: $eventId) {
    id
    rsvps(userId: $userId) {
      id
      beginMeetingTime
      endMeetingTime
    }
  }
}
    `;

export const useSessionMemberQuery = <TData = Schema.SessionMemberQuery, TError = unknown>(
  variables: Schema.SessionMemberQueryVariables,
  options?: Omit<UseQueryOptions<Schema.SessionMemberQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.SessionMemberQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.SessionMemberQuery, TError, TData>({
    queryFn: useFetch<Schema.SessionMemberQuery, Schema.SessionMemberQueryVariables>(SessionMemberDocument).bind(
      null,
      variables,
    ),
    queryKey: ["SessionMember", variables],
    ...options,
  });
};

useSessionMemberQuery.getKey = (variables: Schema.SessionMemberQueryVariables) => ["SessionMember", variables];

export const SessionMemberManagementDocument = `
    query SessionMemberManagement($id: ID!) {
  session(eventId: $id) {
    isPublic
    facilitators {
      id
    }
    members {
      id
      roles
      isVetted
      email
      firstName
      lastName
    }
    owner {
      id
      roles
      isVetted
      email
      firstName
      lastName
    }
    requests {
      id
      roles
      isVetted
      email
      firstName
      lastName
    }
  }
}
    `;

export const useSessionMemberManagementQuery = <TData = Schema.SessionMemberManagementQuery, TError = unknown>(
  variables: Schema.SessionMemberManagementQueryVariables,
  options?: Omit<UseQueryOptions<Schema.SessionMemberManagementQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.SessionMemberManagementQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.SessionMemberManagementQuery, TError, TData>({
    queryFn: useFetch<Schema.SessionMemberManagementQuery, Schema.SessionMemberManagementQueryVariables>(
      SessionMemberManagementDocument,
    ).bind(null, variables),
    queryKey: ["SessionMemberManagement", variables],
    ...options,
  });
};

useSessionMemberManagementQuery.getKey = (variables: Schema.SessionMemberManagementQueryVariables) => [
  "SessionMemberManagement",
  variables,
];

export const SessionMembersDocument = `
    query SessionMembers($id: ID!) {
  session(eventId: $id) {
    isPublic
    facilitators {
      id
    }
    members {
      id
      roles
      isVetted
      email
      firstName
      lastName
    }
    owner {
      id
      roles
      isVetted
      email
      firstName
      lastName
    }
  }
}
    `;

export const useSessionMembersQuery = <TData = Schema.SessionMembersQuery, TError = unknown>(
  variables: Schema.SessionMembersQueryVariables,
  options?: Omit<UseQueryOptions<Schema.SessionMembersQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.SessionMembersQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.SessionMembersQuery, TError, TData>({
    queryFn: useFetch<Schema.SessionMembersQuery, Schema.SessionMembersQueryVariables>(SessionMembersDocument).bind(
      null,
      variables,
    ),
    queryKey: ["SessionMembers", variables],
    ...options,
  });
};

useSessionMembersQuery.getKey = (variables: Schema.SessionMembersQueryVariables) => ["SessionMembers", variables];

export const SessionNameDocument = `
    query SessionName($eventId: ID!) {
  session(eventId: $eventId) {
    id
    name
  }
}
    `;

export const useSessionNameQuery = <TData = Schema.SessionNameQuery, TError = unknown>(
  variables: Schema.SessionNameQueryVariables,
  options?: Omit<UseQueryOptions<Schema.SessionNameQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.SessionNameQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.SessionNameQuery, TError, TData>({
    queryFn: useFetch<Schema.SessionNameQuery, Schema.SessionNameQueryVariables>(SessionNameDocument).bind(
      null,
      variables,
    ),
    queryKey: ["SessionName", variables],
    ...options,
  });
};

useSessionNameQuery.getKey = (variables: Schema.SessionNameQueryVariables) => ["SessionName", variables];

export const SessionRequestsDocument = `
    query SessionRequests($id: ID!) {
  session(eventId: $id) {
    requests {
      id
      roles
      isVetted
      email
      firstName
      lastName
    }
  }
}
    `;

export const useSessionRequestsQuery = <TData = Schema.SessionRequestsQuery, TError = unknown>(
  variables: Schema.SessionRequestsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.SessionRequestsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.SessionRequestsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.SessionRequestsQuery, TError, TData>({
    queryFn: useFetch<Schema.SessionRequestsQuery, Schema.SessionRequestsQueryVariables>(SessionRequestsDocument).bind(
      null,
      variables,
    ),
    queryKey: ["SessionRequests", variables],
    ...options,
  });
};

useSessionRequestsQuery.getKey = (variables: Schema.SessionRequestsQueryVariables) => ["SessionRequests", variables];

export const SessionSummaryDocument = `
    query SessionSummary($eventId: ID!) {
  session(eventId: $eventId) {
    id
    bubbleId
    ownerId
    name
    description
    maxParticipants
    userIsFacilitator
    userIsMember
    userIsVetted
    userIsOwner
    roomId
    members {
      id
      firstName
      lastName
      email
      roles
    }
    meetingTimes {
      capacity
      id
      seriesId
      beginMeetingTime
      endMeetingTime
      userHasRsvpd
      eventId
    }
    eventMilestones {
      id
      description
      dueDate
      name
      eventId
    }
    eventTasks {
      id
      description
      dueDate
      name
      eventId
      userId
    }
    resources {
      id
      name
      description
      url
    }
    isPublic
    isSearchable
    userIsPending
    rules {
      name
      description
      order
    }
    series {
      id
      name
      description
      meetingTimes {
        id
        eventName
        beginMeetingTime
        endMeetingTime
      }
    }
  }
}
    `;

export const useSessionSummaryQuery = <TData = Schema.SessionSummaryQuery, TError = unknown>(
  variables: Schema.SessionSummaryQueryVariables,
  options?: Omit<UseQueryOptions<Schema.SessionSummaryQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.SessionSummaryQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.SessionSummaryQuery, TError, TData>({
    queryFn: useFetch<Schema.SessionSummaryQuery, Schema.SessionSummaryQueryVariables>(SessionSummaryDocument).bind(
      null,
      variables,
    ),
    queryKey: ["SessionSummary", variables],
    ...options,
  });
};

useSessionSummaryQuery.getKey = (variables: Schema.SessionSummaryQueryVariables) => ["SessionSummary", variables];

export const UpcomingRsvpsDocument = `
    query UpcomingRsvps($roomId: ID!, $start: DateTime!, $end: DateTime!) {
  room(roomId: $roomId) {
    roomUpcomingRsvps(start: $start, end: $end) {
      meetingTime {
        beginMeetingTime
        endMeetingTime
      }
      event {
        id
        name
      }
      user {
        phoneNumber
        email
        firstName
        lastName
      }
    }
  }
}
    `;

export const useUpcomingRsvpsQuery = <TData = Schema.UpcomingRsvpsQuery, TError = unknown>(
  variables: Schema.UpcomingRsvpsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.UpcomingRsvpsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.UpcomingRsvpsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.UpcomingRsvpsQuery, TError, TData>({
    queryFn: useFetch<Schema.UpcomingRsvpsQuery, Schema.UpcomingRsvpsQueryVariables>(UpcomingRsvpsDocument).bind(
      null,
      variables,
    ),
    queryKey: ["UpcomingRsvps", variables],
    ...options,
  });
};

useUpcomingRsvpsQuery.getKey = (variables: Schema.UpcomingRsvpsQueryVariables) => ["UpcomingRsvps", variables];

export const GetUserNotificationsDocument = `
    query GetUserNotifications {
  me {
    id
    notifications {
      id
      dateTime
      description
      isRead
      isArchived
      roomId
      roomName
      notificationType
      eventName
      relativeUri
    }
  }
}
    `;

export const useGetUserNotificationsQuery = <TData = Schema.GetUserNotificationsQuery, TError = unknown>(
  variables?: Schema.GetUserNotificationsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.GetUserNotificationsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.GetUserNotificationsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.GetUserNotificationsQuery, TError, TData>({
    queryFn: useFetch<Schema.GetUserNotificationsQuery, Schema.GetUserNotificationsQueryVariables>(
      GetUserNotificationsDocument,
    ).bind(null, variables),
    queryKey: variables === undefined ? ["GetUserNotifications"] : ["GetUserNotifications", variables],
    ...options,
  });
};

useGetUserNotificationsQuery.getKey = (variables?: Schema.GetUserNotificationsQueryVariables) =>
  variables === undefined ? ["GetUserNotifications"] : ["GetUserNotifications", variables];

export const VolunteerStatsDocument = `
    query VolunteerStats {
  volunteerStats {
    minutesThisMonth
    minutesScheduledThisNextYear
    minutesThisYear
    minutesAllTime
    minutesUnconfirmed
  }
}
    `;

export const useVolunteerStatsQuery = <TData = Schema.VolunteerStatsQuery, TError = unknown>(
  variables?: Schema.VolunteerStatsQueryVariables,
  options?: Omit<UseQueryOptions<Schema.VolunteerStatsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Schema.VolunteerStatsQuery, TError, TData>["queryKey"];
  },
) => {
  return useQuery<Schema.VolunteerStatsQuery, TError, TData>({
    queryFn: useFetch<Schema.VolunteerStatsQuery, Schema.VolunteerStatsQueryVariables>(VolunteerStatsDocument).bind(
      null,
      variables,
    ),
    queryKey: variables === undefined ? ["VolunteerStats"] : ["VolunteerStats", variables],
    ...options,
  });
};

useVolunteerStatsQuery.getKey = (variables?: Schema.VolunteerStatsQueryVariables) =>
  variables === undefined ? ["VolunteerStats"] : ["VolunteerStats", variables];
